import { useEffect, useRef, useState } from "react";

import type { Settings } from "@go-novus/admin-api";
import { SegmentedControl } from "@go-novus/ui";
import { DEFAULT_PANEL_SIZE, mountWidgetPreview } from "@go-novus/widget";

import * as m from "#/paraglide/messages";

import type {
  PreviewControl,
  PreviewDevice,
  PreviewView,
} from "../../hooks/use-preview-control";

const VIEWS: { value: PreviewView; label: () => string }[] = [
  { value: "conversation", label: m.settings_preview_conversation },
  { value: "launcher", label: m.settings_preview_launcher },
  { value: "proactive", label: m.settings_preview_proactive },
];

const DEVICES: PreviewDevice[] = ["desktop", "mobile"];

const DEVICE_LABEL: Record<PreviewDevice, () => string> = {
  desktop: m.settings_preview_desktop,
  mobile: m.settings_preview_mobile,
};

/** 店面占位商品卡，数量够铺满最高的舞台就行。 */
const PRODUCTS = Array.from({ length: 12 }, (_, index) => index);
/** 店面示意里的占位色块，买家店面是浅色，写死不跟后台主题。 */
const SHADE = "rgba(23,23,26,0.1)";

/** 手机框取偏矮的机型比例，窄舞台里缩得少一些。 */
const MOBILE_FRAME = { width: 390, height: 720 };
/** 手机框在舞台里上下左右各留的空白，是屏幕上的像素，不随缩放变。 */
const MOBILE_INSET = 24;
/**
 * 桌面场景只比面板多一圈 24px 留白，贴在舞台底角缩放；
 * 投影溢出到场景外的店面背景上，只在舞台边缘被裁掉。
 */
const DESKTOP_SCENE = {
  width: DEFAULT_PANEL_SIZE.width + 48,
  height: DEFAULT_PANEL_SIZE.height + 48,
};
/**
 * 手机框的 `outline` 宽度。描边画在框外，缩放时要连它一起塞进视口，
 * 否则框顶满视口的那两条边会被视口的 overflow-hidden 裁掉。
 */
const FRAME_OUTLINE = 1;

export interface ChatboxPreviewProps extends PreviewControl {
  agentName: string;
  chatbox: Settings["chatbox"];
  logoUrl: string | null;
  /** 主动提示显示的模板原文；不传时挂件回退示例文字。 */
  promptTemplate?: string;
  /** 这条规则的预填问题模板原文；点开提示时进输入框，没传就空着。 */
  questionTemplate?: string;
  /** 已经合法的主题色。商家敲坏 Hex 时上游会守住上一个合法值，这里不再判一次。 */
  themeColor: string;
}

/**
 * 预览挂的是店面那一个 WidgetPanel，外观走 readAppearance——和店面同一张映射表，
 * 所以商家在这里调的每一项都由挂件自己渲染，改了挂件预览就跟着变。
 *
 * 店面背景的颜色刻意写死不走 token：那是买家在店面看到的样子，
 * 跟着后台的深浅色主题变反而是错的。手机框的边和圆角走 token，它是后台的家具。
 */
export function ChatboxPreview({
  agentName,
  chatbox,
  device,
  logoUrl,
  onDeviceChange,
  onViewChange,
  promptTemplate,
  questionTemplate,
  themeColor,
  view,
}: ChatboxPreviewProps) {
  const [available, setAvailable] = useState({ width: 0, height: 0 });
  const viewport = useRef<HTMLDivElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const unmount = useRef<(() => void) | undefined>(undefined);
  const mobile = device === "mobile";
  const margin = mobile ? (FRAME_OUTLINE + MOBILE_INSET) * 2 : 0;
  const scene = mobile ? MOBILE_FRAME : DESKTOP_SCENE;
  // 首帧还没量到尺寸时可用空间是 0，扣掉留白会成负数、把场景镜像翻转，所以兜底到 0。
  const scale = Math.max(
    0,
    Math.min(
      1,
      (available.width - margin) / scene.width,
      (available.height - margin) / scene.height,
    ),
  );
  const left = chatbox.position === "bottom_left";

  useEffect(() => {
    const container = viewport.current;
    if (container === null) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) return;
      setAvailable({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(
    () => () => {
      unmount.current?.();
    },
    [],
  );

  useEffect(() => {
    const container = host.current;

    if (container === null || !chatbox.storefrontEnabled) {
      unmount.current?.();
      unmount.current = undefined;
      return;
    }

    // 更新草稿时保留 Preact 实例；只在隐藏预览或离开页面时卸载。
    unmount.current = mountWidgetPreview(
      container,
      {
        agent: { name: agentName },
        chatbox: { ...chatbox, themeColor },
        logoUrl,
      },
      {
        fill: mobile,
        view,
        promptTemplates: { promptTemplate, questionTemplate },
      },
    );
  }, [
    agentName,
    chatbox,
    logoUrl,
    mobile,
    promptTemplate,
    questionTemplate,
    themeColor,
    view,
  ]);

  return (
    <>
      <h2 className="sr-only">{m.settings_preview_heading()}</h2>
      {/* 预览栏最窄时两组切换放不下一行，折成两行。 */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-surface-default)] px-4.5 py-3">
        <SegmentedControl
          hideLabel
          label={m.settings_preview_view_label()}
          options={VIEWS.map(({ value, label }) => ({ value, label: label() }))}
          value={view}
          onChange={(value) => {
            const selected = VIEWS.find((item) => item.value === value);
            if (selected) onViewChange(selected.value);
          }}
        />
        <SegmentedControl
          hideLabel
          label={m.settings_preview_device_label()}
          onChange={(value) => {
            onDeviceChange(value === "mobile" ? "mobile" : "desktop");
          }}
          options={DEVICES.map((value) => ({
            label: DEVICE_LABEL[value](),
            value,
          }))}
          value={device}
        />
      </div>
      {/* 舞台量自己的可用空间，只把场景等比缩放；桌面的店面背景铺满舞台、不参与缩放。 */}
      <div
        className="relative min-h-0 flex-1 overflow-hidden bg-[var(--bg-surface-subtle)]"
        data-testid="widget-preview-viewport"
        ref={viewport}
        style={mobile ? undefined : STOREFRONT_STYLE}
      >
        {!mobile && <Storefront columns={3} />}
        <div
          className={
            mobile
              ? "absolute left-1/2 top-1/2 origin-center overflow-hidden rounded-[var(--radius)] outline outline-[var(--border-surface-default)]"
              : `absolute bottom-0 ${left ? "left-0 origin-bottom-left" : "right-0 origin-bottom-right"}`
          }
          data-testid="widget-preview-scene"
          style={{
            ...(mobile ? STOREFRONT_STYLE : {}),
            width: scene.width,
            height: scene.height,
            transform: mobile
              ? `translate(-50%, -50%) scale(${String(scale)})`
              : `scale(${String(scale)})`,
          }}
        >
          {mobile && <Storefront columns={2} />}
          {chatbox.storefrontEnabled ? (
            <div
              // 桌面只钉底边：挂件从容器顶部往下排，钉满四边会把收起入口顶到场景上方。
              className={
                mobile ? "absolute inset-0" : "absolute bottom-6 left-6 right-6"
              }
              data-testid="widget-preview-host"
              ref={host}
            />
          ) : (
            <div
              className="absolute inset-x-4 bottom-4.5 rounded-md border border-dashed px-3 py-2.5 text-micro"
              style={{
                borderColor: "rgba(23,23,26,0.2)",
                color: "#66666e",
              }}
            >
              {m.settings_preview_hidden()}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const STOREFRONT_STYLE = { background: "#ffffff", color: "#17171a" };

/** 店面示意：店招、横幅和商品卡。毛玻璃和面板投影得有东西压在下面才看得出来。 */
function Storefront({ columns }: { columns: 2 | 3 }) {
  return (
    <div aria-hidden>
      <div
        className="flex h-13.5 items-center justify-between border-b px-4.5"
        style={{ borderColor: SHADE }}
      >
        <span className="text-body font-bold [letter-spacing:0.08em]">
          {m.settings_preview_store_name()}
        </span>
        <div className="flex gap-3">
          {[0, 1, 2].map((item) => (
            <div
              className="h-2 w-9 rounded-full"
              key={item}
              style={{ background: SHADE }}
            />
          ))}
        </div>
      </div>
      <div
        className="mx-4.5 mt-4.5 flex h-32 flex-col justify-center gap-2.5 rounded-md px-6"
        style={{ background: SHADE }}
      >
        <div className="h-3 w-2/5 rounded-full" style={{ background: SHADE }} />
        <div
          className="h-6 w-20 rounded-full"
          style={{ background: "#17171a" }}
        />
      </div>
      <div
        className={`grid gap-4 p-4.5 ${columns === 2 ? "grid-cols-2" : "grid-cols-3"}`}
      >
        {PRODUCTS.map((product) => (
          <div key={product}>
            <div
              className="aspect-[3/4] rounded-md"
              style={{ background: SHADE }}
            />
            <div
              className="mt-2 h-2.5 w-3/4 rounded-full"
              style={{ background: SHADE }}
            />
            <div
              className="mt-1.5 h-2.5 w-1/3 rounded-full"
              style={{ background: SHADE }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
